import { DeleteOutline } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { useConfirmationDialog } from "../../../contexts/ConfirmationDialogContext";
import {
  removeTalentBankInfo,
  removeTalentSsn,
} from "../../talents/talentsSlice";
import { RemoveTalentPaymentInformation } from "../../talents/types/commands";
import { AccessRightContext } from "../../user/AccessRightProvider";

type Props = RemoveTalentPaymentInformation & {
  field: "ssn" | "bankInfo";
  disabled: boolean;
};

export const RemovePaymentInformationButton = ({
  talentId,
  version,
  field,
  disabled,
}: Props) => {
  const { isAdmin } = useContext(AccessRightContext);
  const { t } = useTranslation();
  const { confirm } = useConfirmationDialog();
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState(false);
  const label = t(`salaryInformation:remove_${field}`);

  if (!isAdmin) return null;

  const remove = async () => {
    if (!isAdmin || disabled || pending) return;
    setPending(true);
    try {
      const confirmed = await confirm({
        title: label,
        contentText: t(`salaryInformation:confirmRemove_${field}`),
        confirmText: t("common:delete"),
        ConfirmButtonProps: { color: "error" },
      });
      if (confirmed) {
        const command =
          field === "ssn" ? removeTalentSsn : removeTalentBankInfo;
        await dispatch(command({ talentId, version }));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          aria-label={label}
          size="small"
          disabled={disabled || pending}
          onClick={remove}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};
